/**
 * AreaCommandDirectivesSection (R5 Slice 5, #541) — the Area Command PARENT's OWN
 * doctrine content: two ordered free-text capture/edit blocks on the is_area_command
 * parent (Ontario IMS §4.6 / Appendix A #2, verbatim):
 *
 *   "Area command has the responsibility to develop BROAD OBJECTIVES for their assigned
 *    area and set PRIORITIES FOR THE SHARING OF CRITICAL RESOURCES across incident sites."
 *
 *  - BROAD OBJECTIVES — the Area Command's own broad objectives for the assigned area.
 *    Heading KEEPS "broad" (the App A #40 *Incident objectives* collision guard); NEVER
 *    "IAP" / "strategies and tactics" / operational-period framing (that is a CHILD's plan
 *    — §4.6: each grouped incident keeps its own IMT).
 *  - RESOURCE PRIORITIES — priority STATEMENTS for the sharing of critical resources
 *    across the grouped sites. NEVER "assign" / "allocate" / "order to" / "task" / "direct"
 *    a child (§4.6: the area command "works with" the participating IMTs; it does not
 *    command them). NOT the per-incident "Critical Resource Needs" request rollup.
 *
 * Each block edits a newline-delimited ordered list (one item per line); Save PUTs the
 * WHOLE ordered list (replace-whole-ordered-list — the line order is the ordinal). No
 * person field, no allocation math, no resource-count — App A #2 is priority STATEMENTS.
 * Extracted from IncidentManagementSection so the vocabulary lock can render this surface
 * directly. Pinned by ontarioImsVocabulary.lock.test.ts + areaCommandParentPanel.lock.test.tsx.
 */
import { useEffect, useState, type JSX } from 'react';
import {
  AREA_COMMAND_OBJECTIVES_HEADING,
  AREA_COMMAND_RESOURCE_PRIORITIES_HEADING,
  AREA_COMMAND_OBJECTIVES_EMPTY,
  AREA_COMMAND_RESOURCE_PRIORITIES_EMPTY,
  AREA_COMMAND_OBJECTIVES_CAPTION,
  AREA_COMMAND_RESOURCE_PRIORITIES_CAPTION,
  SAVE_AREA_COMMAND_OBJECTIVES_LABEL,
  SAVE_AREA_COMMAND_RESOURCE_PRIORITIES_LABEL,
} from '../incidentVocabulary';

/** Split a textarea's value into an ordered list: one item per non-blank line, trimmed. */
function toOrderedList(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

interface OrderedListEditorProps {
  testidPrefix: string;
  heading: string;
  caption: string;
  emptyState: string;
  saveLabel: string;
  items: ReadonlyArray<string>;
  busy: boolean;
  onSave: (items: string[]) => void;
}

/** One ordered free-text capture/edit block (shared by objectives + resource priorities). */
function OrderedListEditor({
  testidPrefix,
  heading,
  caption,
  emptyState,
  saveLabel,
  items,
  busy,
  onSave,
}: OrderedListEditorProps): JSX.Element {
  const saved = items.join('\n');
  const [text, setText] = useState(saved);
  // Re-sync the editor to the persisted list whenever it changes (e.g. after a Save
  // round-trips through a refetch). `saved` is a primitive, so this only fires on a real
  // content change — it never clobbers an in-flight edit (the saved list is stable then).
  useEffect(() => setText(saved), [saved]);

  const nextList = toOrderedList(text);
  const dirty = nextList.join('\n') !== items.join('\n');

  return (
    <div
      data-testid={testidPrefix}
      className="rounded border border-indigo-500/40 bg-indigo-500/5 px-3 py-2"
    >
      <h4 className="text-[10px] uppercase tracking-wider text-indigo-300">{heading}</h4>
      <p className="mt-0.5 text-[10px] leading-tight text-slate-500">{caption}</p>
      <textarea
        data-testid={`${testidPrefix}-input`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
        rows={Math.min(Math.max(nextList.length + 1, 3), 8)}
        placeholder={`${emptyState} One per line.`}
        className="mt-1 w-full resize-y rounded border border-slate-600/60 bg-slate-900/60 px-2 py-1 text-[12px] text-slate-200 placeholder:text-slate-600 focus:border-indigo-400/60 focus:outline-none disabled:opacity-50"
      />
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-500" data-testid={`${testidPrefix}-count`}>
          {nextList.length === 0 ? emptyState : `${nextList.length} ordered`}
        </span>
        <button
          type="button"
          data-testid={`${testidPrefix}-save`}
          disabled={busy || !dirty}
          onClick={() => onSave(nextList)}
          className="shrink-0 rounded border border-indigo-500/60 px-2 py-0.5 text-[10px] text-indigo-200 hover:bg-indigo-600/20 disabled:opacity-50"
        >
          {busy ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

export interface AreaCommandDirectivesSectionProps {
  /** The Area Command's ordered broad objectives (IncidentDetail#area_command_objectives). */
  objectives: ReadonlyArray<string>;
  /** The Area Command's ordered resource-sharing priorities. */
  resourcePriorities: ReadonlyArray<string>;
  busy: boolean;
  onSaveObjectives: (objectives: string[]) => void;
  onSaveResourcePriorities: (priorities: string[]) => void;
}

export function AreaCommandDirectivesSection({
  objectives,
  resourcePriorities,
  busy,
  onSaveObjectives,
  onSaveResourcePriorities,
}: AreaCommandDirectivesSectionProps): JSX.Element {
  return (
    <div className="space-y-2" data-testid="area-command-directives">
      <OrderedListEditor
        testidPrefix="area-command-objectives"
        heading={AREA_COMMAND_OBJECTIVES_HEADING}
        caption={AREA_COMMAND_OBJECTIVES_CAPTION}
        emptyState={AREA_COMMAND_OBJECTIVES_EMPTY}
        saveLabel={SAVE_AREA_COMMAND_OBJECTIVES_LABEL}
        items={objectives}
        busy={busy}
        onSave={onSaveObjectives}
      />
      <OrderedListEditor
        testidPrefix="area-command-resource-priorities"
        heading={AREA_COMMAND_RESOURCE_PRIORITIES_HEADING}
        caption={AREA_COMMAND_RESOURCE_PRIORITIES_CAPTION}
        emptyState={AREA_COMMAND_RESOURCE_PRIORITIES_EMPTY}
        saveLabel={SAVE_AREA_COMMAND_RESOURCE_PRIORITIES_LABEL}
        items={resourcePriorities}
        busy={busy}
        onSave={onSaveResourcePriorities}
      />
    </div>
  );
}
